import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { MainLink, CreateMainLinkData, ThemeSettings } from '@/types/link-short';
import { useAuth } from '@/hooks/use-auth';
import { toast } from '@/hooks/use-toast';

export function useMainLinks() {
  const { user } = useAuth();
  const [mainLinks, setMainLinks] = useState<MainLink[]>([]);
  const [loading, setLoading] = useState(true);

  const safeParseThemeSettings = (themeSettings: any): ThemeSettings => {
    if (!themeSettings || typeof themeSettings !== 'object') {
      return {
        backgroundType: 'solid',
        backgroundColor: '#f3f4f6',
        textColor: '#1f2937',
        buttonStyle: 'filled',
        buttonBgColor: '#3b82f6',
        buttonTextColor: '#ffffff',
        borderRadius: '0.5rem'
      } as ThemeSettings;
    }
    
    return {
      backgroundType: themeSettings.backgroundType || 'solid',
      backgroundColor: themeSettings.backgroundColor || '#f3f4f6',
      textColor: themeSettings.textColor || '#1f2937',
      buttonStyle: themeSettings.buttonStyle || 'filled',
      buttonBgColor: themeSettings.buttonBgColor || '#3b82f6',
      buttonTextColor: themeSettings.buttonTextColor || '#ffffff',
      borderRadius: themeSettings.borderRadius || '0.5rem',
      backgroundGradient: themeSettings.backgroundGradient,
      backgroundImageUrl: themeSettings.backgroundImageUrl,
      cardBackgroundColor: themeSettings.cardBackgroundColor,
      fontFamily: themeSettings.fontFamily,
      avatarStyle: themeSettings.avatarStyle || 'circle',
      layout: themeSettings.layout || 'default',
      buttonAnimation: themeSettings.buttonAnimation || 'none',
      iconPosition: themeSettings.iconPosition || 'left',
      showBio: themeSettings.showBio !== false,
      iconSize: themeSettings.iconSize || 'medium'
    } as ThemeSettings;
  };

  const fetchMainLinks = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('main_links')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const transformedData: MainLink[] = (data || []).map(item => ({
        id: item.id,
        user_id: item.user_id,
        title: item.title,
        slug: item.slug,
        description: item.description,
        url: item.url,
        is_active: item.is_active,
        clicks: item.clicks,
        theme_settings: safeParseThemeSettings(item.theme_settings),
        bg_color: item.bg_color,
        text_color: item.text_color,
        avatar_url: item.avatar_url,
        created_at: item.created_at,
        updated_at: item.updated_at
      }));
      
      setMainLinks(transformedData);
    } catch (error) {
      console.error('Error fetching main links:', error);
      toast.error("Erro ao carregar links");
    } finally {
      setLoading(false);
    }
  };

  const createMainLink = async (data: CreateMainLinkData) => {
    if (!user) return null;

    try {
      const { data: newLink, error } = await supabase
        .from('main_links')
        .insert({
          ...data,
          user_id: user.id,
          theme_settings: data.theme_settings || {}
        })
        .select()
        .single();

      if (error) throw error;
      
      const transformedLink: MainLink = {
        id: newLink.id,
        user_id: newLink.user_id,
        title: newLink.title,
        slug: newLink.slug,
        description: newLink.description,
        url: newLink.url,
        is_active: newLink.is_active,
        clicks: newLink.clicks,
        theme_settings: safeParseThemeSettings(newLink.theme_settings),
        bg_color: newLink.bg_color,
        text_color: newLink.text_color,
        avatar_url: newLink.avatar_url,
        created_at: newLink.created_at,
        updated_at: newLink.updated_at
      };
      
      setMainLinks(prev => [transformedLink, ...prev]);
      toast.success("Link criado com sucesso!");
      return transformedLink;
    } catch (error: any) {
      console.error('Error creating main link:', error);
      if (error.code === '23505') {
        toast.error("Este slug já está em uso. Escolha outro.");
      } else {
        toast.error("Erro ao criar link");
      }
      return null;
    }
  };

  const updateMainLink = async (id: string, updates: Partial<MainLink>) => {
    try {
      const { error } = await supabase
        .from('main_links')
        .update({
          ...updates,
          theme_settings: updates.theme_settings || {}
        })
        .eq('id', id);

      if (error) throw error;
      
      setMainLinks(prev => 
        prev.map(link => 
          link.id === id ? { ...link, ...updates } : link
        )
      );
      toast.success("Link atualizado com sucesso!");
      return true;
    } catch (error) {
      console.error('Error updating main link:', error);
      toast.error("Erro ao atualizar link");
      return false;
    }
  };

  const deleteMainLink = async (id: string) => {
    try {
      const { error } = await supabase
        .from('main_links')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setMainLinks(prev => prev.filter(link => link.id !== id));
      toast.success("Link excluído com sucesso!");
      return true;
    } catch (error) {
      console.error('Error deleting main link:', error);
      toast.error("Erro ao excluir link");
      return false;
    }
  };

  const fetchMainLinkBySlug = async (slug: string) => {
    try {
      console.log('fetchMainLinkBySlug called with slug:', slug);
      
      const { data, error } = await supabase
        .from('main_links')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .maybeSingle();

      console.log('fetchMainLinkBySlug result:', { data, error });

      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      
      if (!data) {
        console.log('No data found for slug:', slug);
        return null;
      }
      
      return {
        id: data.id,
        user_id: data.user_id,
        title: data.title,
        slug: data.slug,
        description: data.description,
        url: data.url,
        is_active: data.is_active,
        clicks: data.clicks,
        theme_settings: safeParseThemeSettings(data.theme_settings),
        bg_color: data.bg_color,
        text_color: data.text_color,
        avatar_url: data.avatar_url,
        created_at: data.created_at,
        updated_at: data.updated_at
      } as MainLink;
    } catch (error) {
      console.error('Error fetching main link by slug:', error);
      return null;
    }
  };

  const incrementMainLinkClicks = async (id: string) => {
    try {
      await supabase.rpc('increment_main_link_clicks', { link_id: id });
    } catch (error) {
      console.error('Error incrementing clicks:', error);
    }
  };

  useEffect(() => {
    fetchMainLinks();
  }, [user]);

  return {
    mainLinks,
    loading,
    createMainLink,
    updateMainLink,
    deleteMainLink,
    fetchMainLinkBySlug,
    incrementMainLinkClicks,
    refetch: fetchMainLinks
  };
}
